import {MouseEventHandler, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../../api/types/commercialCategory";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import SearchFilters from "../../../common/components/SearchFilters";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {excessGroupFiltersAtom} from "../../../state/excessGroupsRecoil";
import {fromGenericBeToGenericFe} from "../../../utils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const ExcessGroupFilters = ({
                                handleClickResetSearch,
                                handleClickSearch,
                                testFilters,
                                setTestFilters
                            }: {
    handleClickSearch: MouseEventHandler<HTMLButtonElement>;
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>;
    testFilters: any
    setTestFilters: any
}) => {
    // Use Recoil
    const [excessGroupsFilters, setExcessGroupsFilters] = useRecoilState(excessGroupFiltersAtom);
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Repository Hooks
    const {getCommercialCategories} = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // Use State
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    // Carica commercial segments e categories all'inizio
    useEffect(() => {
        const getContent = async () => {
            if (!commercialSegmentList?.length) {
                const commercialSegmentsData = await getCommercialSegments();
                const commercialSegmentList: SelectElementModelFE[] = fromGenericBeToGenericFe(commercialSegmentsData);
                setCommercialSegmentList(commercialSegmentList);
            }

            const commercialCategoriesData: CommercialCategoryEntityResponse[] = await getCommercialCategories();
            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryList(commercialCategoryList);
            setCommercialCategoryListOriginal(commercialCategoryList);
        };
        getContent();
        // eslint-disable-next-line
    }, []);

    // Filtra commercial categories quando cambiano legal entities o commercial segment
    useEffect(() => {
        // Se nessuna legal entity selezionata, svuota la lista
        if (!testFilters.legalEntities || 
            (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0)) {
            setCommercialCategoryList([]);
            return;
        }

        // Ottieni le legal entities selezionate
        const selectedLegalEntities = Array.isArray(testFilters.legalEntities)
            ? testFilters.legalEntities.map((le: SelectElementModelFE) => le.code as string)
            : [testFilters.legalEntities.code as string];

        // Filtra per commercial segment se selezionato
        let filteredBySegment = commercialCategoryListOriginal;
        if (testFilters.commercialSegmentId) {
            filteredBySegment = commercialCategoryListOriginal.filter(
                cc => cc.commercialSegment.id === (testFilters.commercialSegmentId as SelectElementModelFE).code
            );
        }

        // Espandi le categorie in base alle legal entities selezionate e deduplica per label
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        
        filteredBySegment.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (selectedLegalEntities.includes(labelObj.country)) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });

        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [testFilters.legalEntities, testFilters.commercialSegmentId, commercialCategoryListOriginal]);

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "name",
            value: testFilters.name as string,
            label: `${t("pricing:excessGroup.filter.labels.name")}`,
            handleChange: (value: string) => setTestFilters({...testFilters, name: value}),
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegment",
            value: testFilters.commercialSegmentId as SelectElementModelFE,
            label: `${t("pricing:excessGroup.filter.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null
                }),
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategory",
            value: testFilters.commercialCategoryId as SelectElementModelFE,
            label: `${t("pricing:excessGroup.filter.labels.commercialCategory")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                }),
            options: commercialCategoryList,
            type: "Autocomplete",
            disabled: !testFilters.legalEntities || 
                     (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0),
        }
    ];
    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "reservationFrom",
            value: testFilters.validityDateFrom as string,
            options: [],
            label: `${t("pricing:excessGroup.filter.labels.startDateValidity")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                validityDateFrom: value
            }),
            type: "Date"
        },
        {
            name: "reservationTo",
            value: testFilters.validityDateTo as string,
            options: [],
            label: `${t("pricing:excessGroup.filter.labels.endDateValidity")}`,
            handleChange: (value: string) => setTestFilters({...testFilters, validityDateTo: value}),
            type: "Date"
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as any, // Cast per supportare array
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.legalEntity"
            )}*`,
            handleChange: (value: SelectElementModelFE[]) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                    commercialCategoryId: null,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false,
            error: !testFilters.legalEntities || 
                   (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0),
            helperText: (!testFilters.legalEntities || 
                        (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0))
                ? t("translation:validation.selectAtLeastOne")
                : undefined,
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    return (
        // <CardContainer>
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleClickSearch}
            handleClickResetSearch={handleClickResetSearch}
        />
        // </CardContainer>
    );
};

export default ExcessGroupFilters;
